import { DefaultButton, DialogFooter, Label, MessageBar, MessageBarType, PrimaryButton, ProgressIndicator, Stack, TextField } from "@fluentui/react"
import { useCallback, useEffect, useState } from "react"
import { useApiCall } from "../hooks/apiHooks"
import { getGenderValueFromGender, getRaterTierFormattedFromCoverageTypeValue, safeFormatDateValue } from "../lib/helper"
import { makeApiCall } from "../services/apiService"
import { downloadBase64Content } from "../services/helperService"
import { GradientCensusCoverageType, IGrandientCensusInputRow } from "../types/CensusTypes"
import { IAccount } from "../types/IAccount"

interface GradientCensusDialogProps {
	group: IAccount
	onDismiss: () => void
}

type GradientCensusOutput = {
	content: string
	error: string | null
	fileName: string
}

interface ICensusPayload {
	censusData: IGrandientCensusInputRow[]
	groupData: {
		groupName: string
		groupId: string
	}
}

interface IDataverseHouseholdMember {
	cw_householdmembersid: string
	cw_firstname: string
	cw_lastname: string
	cw_relationship: number
	cw_gender: number
	cw_hhzip: string
	cw_birthdate: string
	"cw_birthdate@OData.Community.Display.V1.FormattedValue": string
}

interface IDataverseEmployee {
	"cw_birthdate@OData.Community.Display.V1.FormattedValue": string
	cw_birthdate: string
	cw_firstname: string
	cw_lastname: string
	cw_zipcode: string
	"_cw_plan_value@OData.Community.Display.V1.FormattedValue": string
	_cw_plan_value: string
	"cw_healthcarecoveragetype@OData.Community.Display.V1.FormattedValue": string
	cw_healthcarecoveragetype: number
	cw_employeeid: string
	cw_gender: number
	"cw_gender@OData.Community.Display.V1.FormattedValue": string
	cw_cw_householdmembers_Employee_cw_employee: IDataverseHouseholdMember[]
}

const zipCodePrefixes: string[] = ["201", ...Array.from({ length: 27 }, (_, i) => (220 + i).toString())]

export function GradientCensusForm(props: GradientCensusDialogProps) {
	const { onDismiss, group } = props

	const [groupName, setGroupName] = useState(group.name)
	const [employees, setEmployees] = useState<IDataverseEmployee[]>()
	const [generatingGradientCensusMessage, setGeneratingGradientCensusMessage] = useState<string>("Please be patient as this can take up to a minute...")
	const [gradientCensusPayload, setGradientCensusPayload] = useState<ICensusPayload>()
	const [isPayloadCensusValid, setIsPayloadCensusValid] = useState<boolean>(true)
	const [isZipCodeDataValid, setIsZipCodeDataValid] = useState<boolean>(false)

	useEffect(() => {
		if (employees) {
			let allZipCodesValid = true
			for (let i = 0; i < employees.length; i++) {
				const employee = employees[i]
				if (employee.cw_zipcode && !zipCodePrefixes.some(prefix => employee.cw_zipcode.startsWith(prefix))) {
					allZipCodesValid = false
					break
				}
			}
			setIsZipCodeDataValid(allZipCodesValid)
		}
	}, [employees])

	useEffect(() => {
		if (!isSubmitDisabled()) {
			setGradientCensusPayload(getGradientCensusPayload())
		}
	}, [groupName, employees])

	const gradientCensusFileName = (): string => {
		return `Gradient Census - ${groupName} - ${new Date().toLocaleDateString("en-us", { year: "numeric", month: "2-digit", day: "2-digit" }).replace(/\//g, "")}`
	}

	const getGradientCensusPayload = (): ICensusPayload => {
		if (employees) {
			const plans: any = {}

			const censusData: IGrandientCensusInputRow[] = employees
				.flatMap(e => {
					const employeeCoverage = getRaterTierFormattedFromCoverageTypeValue(e.cw_healthcarecoveragetype)
					const adjustedEmployeeCoverage = employeeCoverage !== "EF" ? (employeeCoverage as GradientCensusCoverageType) : "Fam"
					const censusRetVal: IGrandientCensusInputRow[] = []
					let employeeZip = e.cw_zipcode || group.address1_postalcode
					if (employeeZip) {
						if (e.cw_zipcode && !zipCodePrefixes.some(prefix => e.cw_zipcode.startsWith(prefix))) {
							employeeZip = group.address1_postalcode // If the zip code is invalid, use the group zip code
						}
					}
					censusRetVal.push({
						firstName: e.cw_firstname,
						lastName: e.cw_lastname,
						relationship: "Sub",
						dob: e["cw_birthdate@OData.Community.Display.V1.FormattedValue"],
						gender: getGenderValueFromGender(e.cw_gender) === "Male" ? "M" : "F",
						coverageType: adjustedEmployeeCoverage,
						zipCode: employeeZip
					})
					if (e.cw_cw_householdmembers_Employee_cw_employee?.filter(h => h.cw_relationship !== 0).length > 0) {
						e.cw_cw_householdmembers_Employee_cw_employee.forEach(hh => {
							if (hh.cw_relationship !== 0) {
								let zipCode = hh.cw_hhzip || group.address1_postalcode
								if (zipCode) {
									if (e.cw_zipcode && !zipCodePrefixes.some(prefix => hh.cw_hhzip?.startsWith(prefix))) {
										zipCode = group.address1_postalcode // If the zip code is invalid, use the group zip code
									}
								}
								censusRetVal.push({
									firstName: hh.cw_firstname,
									lastName: hh.cw_lastname,
									relationship: hh.cw_relationship === 2 ? "Sp" : "Ch",
									dob: hh["cw_birthdate@OData.Community.Display.V1.FormattedValue"],
									coverageType: adjustedEmployeeCoverage,
									zipCode: zipCode || employeeZip,
									gender: getGenderValueFromGender(hh.cw_gender) === "Male" ? "M" : "F"
								})
							}
						})
					}
					return censusRetVal
				})
				.filter(e => e)
			if (censusData.length < 5) {
				setIsPayloadCensusValid(false)
			} else {
				setIsPayloadCensusValid(true)
				const retVal: ICensusPayload = {
					censusData: censusData,
					groupData: {
						groupName: groupName,
						groupId: group.accountid
					}
				}
				return retVal
			}
		}

		return undefined
	}

	const {
		data: gradientCensus,
		loading: loadingCensus,
		error: errorGettingCensus,
		fetchData: generateCensus,
		setData: setCensus,
		abort: abortCensus
	} = useApiCall<GradientCensusOutput>(`/getGradientCensusWorkbook?opportunityYear=${safeFormatDateValue(group.cw_coveragestartdate, "yyyy")}`, {
		handlerOnly: true,
		body: JSON.stringify(gradientCensusPayload),
		method: "POST"
	})

	const getExistingHouseholdMembers = useCallback(async () => {
		const body = {
			query: `/cw_employees?$filter=_cw_employer_value eq '${group.accountid}'&$expand=cw_cw_householdmembers_Employee_cw_employee($select=cw_householdmembersid,cw_relationship,cw_firstname,cw_lastname,cw_gender,cw_hhzip,cw_birthdate)&$select=cw_employeeid,cw_birthdate,cw_firstname,cw_lastname,cw_zipcode,_cw_plan_value,cw_healthcarecoveragetype,cw_gender`
		}
		const response = await makeApiCall("/makeCRMGetRequest", "POST", JSON.stringify(body))
		const employees: IDataverseEmployee[] = response.value.filter((e: IDataverseEmployee) => e.cw_healthcarecoveragetype !== 121640001)
		setEmployees(employees)
	}, [group])

	useEffect(() => {
		getExistingHouseholdMembers()
	}, [group, getExistingHouseholdMembers])

	const isSubmitDisabled = () => {
		return !groupName || !employees || loadingCensus
	}
	2
	const downloadGradientCensus = () => {
		if (gradientCensus) {
			downloadBase64Content(gradientCensus.content, gradientCensusFileName(), "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet")
		}
	}

	return (
		<div style={{ maxWidth: 500 }}>
			{employees && employees.length > 4 && (
				<Stack tokens={{ childrenGap: 8 }}>
					<Stack horizontal tokens={{ childrenGap: 4 }} verticalAlign="center">
						<Label>Number of Employees:</Label>
						<span>{employees.length}</span>
					</Stack>
					<TextField label="Group Name" value={groupName} onChange={(_ev, newValue) => setGroupName(newValue || "")} required />
					{loadingCensus && <ProgressIndicator label={`Generating Gradient Census. ${generatingGradientCensusMessage}`} />}
					{errorGettingCensus && <MessageBar messageBarType={MessageBarType.error}>There was an error generating the Gradient Census</MessageBar>}
				</Stack>
			)}
			<DialogFooter>
				{gradientCensus && !loadingCensus && (
					<PrimaryButton styles={{ root: { background: "green" } }} text="Download Gradient Census" iconProps={{ iconName: "Download" }} onClick={downloadGradientCensus} />
				)}
				{loadingCensus && (
					<DefaultButton
						onClick={() => {
							setCensus(undefined)
							abortCensus()
						}}
						text="Cancel"
					/>
				)}
				<PrimaryButton
					onClick={() => {
						setCensus(undefined)
						generateCensus()
						setTimeout(() => {
							setGeneratingGradientCensusMessage("Compling census, about 30 more seconds...")
						}, 20000)
						setTimeout(() => {
							setGeneratingGradientCensusMessage("Almost there....")
						}, 35000)
					}}
					text="Generate Gradient Census"
					disabled={isSubmitDisabled() || !isPayloadCensusValid}
				/>
				<DefaultButton onClick={onDismiss} text="Close" />
			</DialogFooter>
		</div>
	)
}
